import React, { useEffect, useState } from 'react';
import html2canvas from 'html2canvas';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import JSZip from 'jszip';
import './DeckVisualizer.css';

// Register ChartJS components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Custom card rules: card numbers with copy limits exceeding 4
const CARD_RULES = {
  'PR-107': 8,    // Exception: Baltan can have up to 8 copies
  'PR-036': 33    // Exception: can have up to 33 copies
};

function DeckVisualizer() {
  const [cards, setCards] = useState([]);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [importCode, setImportCode] = useState('');
  const [exportCode, setExportCode] = useState('');
  const [showExportCode, setShowExportCode] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [hoveredCardIndex, setHoveredCardIndex] = useState(null);
  const [searchPage, setSearchPage] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(false);
  const [totalLoadedCards, setTotalLoadedCards] = useState(0);
  const [collapseStats, setCollapseStats] = useState(false);
  const [collapseSearch, setCollapseSearch] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = useState(false);
  const [collapseRecommendations, setCollapseRecommendations] = useState(false);
  const [collapseFilters, setCollapseFilters] = useState(true);
  const [selectedCardTypes, setSelectedCardTypes] = useState([]);
  const [selectedCharacters, setSelectedCharacters] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [collapseDrawSimulator, setCollapseDrawSimulator] = useState(false);
  const [drawnCards, setDrawnCards] = useState([]);
  const [remainingDeck, setRemainingDeck] = useState([]);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mulliganCount, setMulliganCount] = useState(0);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Track mobile viewport to disable TTS export on small screens
  useEffect(() => {
    const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
    updateIsMobile();
    window.addEventListener('resize', updateIsMobile);
    return () => window.removeEventListener('resize', updateIsMobile);
  }, []);

  // Helper function to get max copies allowed for a card
  const getMaxCopiesAllowed = (cardNumber) => {
    return CARD_RULES[cardNumber] || 4;
  };

  // Helper function to get image URL (direct access)
  const getProxyImageUrl = (imageUrl) => {
    return imageUrl || '';
  };

  // Deck validation and statistics
  const validateDeck = () => {
    const errors = [];
    const stats = {
      totalCards: 0,
      cardsByLevel: {},
      sceneCards: 0,
      cardCounts: {},
      characterBreakdown: {} // New: character categorization for pie chart
    };

    // Count cards and track duplicates
    cards.forEach(card => {
      const count = parseInt(card.count) || 1;
      stats.totalCards += count;

      console.log('Processing card:', { name: card.name, characterName: card.characterName, type: card.type, level: card.level, number: card.cardNumber || card.number });

      // Check for duplicate limit (4 copies max per card using cardNumber as unique identifier)
      // cardNumber is section+bundleVersion+"-"+serial (e.g., "BP03-077")
      const uniqueCardId = card.cardNumber || card.number || card.id;
      if (!stats.cardCounts[uniqueCardId]) {
        stats.cardCounts[uniqueCardId] = 0;
      }
      stats.cardCounts[uniqueCardId] += count;

      // Extract level from type or card.level field
      const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
      if (typeText.toLowerCase().includes('scene')) {
        stats.sceneCards += count;
        // Add to character breakdown
        if (!stats.characterBreakdown['Scene']) {
          stats.characterBreakdown['Scene'] = 0;
        }
        stats.characterBreakdown['Scene'] += count;
      } else {
        // Get level from card.level field or extract from type
        let level = card.level;
        if (!level) {
          // Try to extract level number from type (e.g., "Level 1", "Lv.2", etc.)
          const levelMatch = typeText.match(/level\s*(\d+)|lv\.?\s*(\d+)|l(\d+)/i);
          level = levelMatch ? (levelMatch[1] || levelMatch[2] || levelMatch[3]) : null;
        }
        
        if (level) {
          const levelKey = `level${level}`;
          stats.cardsByLevel[levelKey] = (stats.cardsByLevel[levelKey] || 0) + count;
        }

        // Categorize by character
        const characterName = card.characterName || 'Unknown';
        
        // Check if it's a special exception character (Belial, Juggulus Juggler)
        const isSpecialCharacter = ['BELIAL', 'JUGGULUS JUGGLER'].includes(characterName.toUpperCase());
        
        // Determine category
        let category;
        if (characterName.toLowerCase().includes('kaiju') || 
            (!isSpecialCharacter && typeText.toLowerCase().includes('kaiju'))) {
          category = 'Ultra Kaijus'; // Aggregate category for kaijus (except exceptions)
        } else {
          category = isSpecialCharacter ? characterName : characterName;
        }

        if (!stats.characterBreakdown[category]) {
          stats.characterBreakdown[category] = 0;
        }
        stats.characterBreakdown[category] += count;
      }
    });

    // Validate rules
    if (stats.totalCards !== 50) {
      if (stats.totalCards < 50) {
        errors.push(`⚠️ Deck has ${stats.totalCards} cards (need exactly 50)`);
      } else {
        errors.push(`⚠️ Deck has ${stats.totalCards} cards (need exactly 50)`);
      }
    }

    Object.entries(stats.cardCounts).forEach(([cardId, count]) => {
      const card = cards.find(c => c.id === cardId);
      const cardNumber = card?.cardNumber || card?.number;
      const maxAllowed = getMaxCopiesAllowed(cardNumber);
      if (count > maxAllowed) {
        errors.push(`⚠️ "${card?.name || cardId}" has ${count} copies (max ${maxAllowed} allowed)`);
      }
    });

    console.log('Final validation stats:', stats);
    return { errors, stats };
  };

  const deckValidation = validateDeck();

  const clearCards = () => {
    setCards([]);
    setShowClearConfirm(false);
  };

  const exportDeck = () => {
    if (cards.length === 0) {
      alert('No cards to export!');
      return;
    }

    try {
      const deckData = {
        version: 2,
        timestamp: new Date().toISOString(),
        cards: cards.map(card => ({
          id: card.id,
          count: card.count
        }))
      };
      
      const deckString = JSON.stringify(deckData);
      const encodedDeck = btoa(unescape(encodeURIComponent(deckString)));
      setExportCode(encodedDeck);
      setShowExportCode(true);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export deck');
    }
  };

  const importDeck = async () => {
    if (!importCode.trim()) {
      alert('Please paste a deck code first!');
      return;
    }

    try {
      const deckString = decodeURIComponent(escape(atob(importCode.trim())));
      const deckData = JSON.parse(deckString);
      
      // Handle both v1 (legacy) and v2 (new) formats
      let importedCards = [];
      
      if (deckData.version === 2 && deckData.cards) {
        // New format with card IDs - fetch full metadata from API
        const cardPromises = deckData.cards.map(entry =>
          fetch(`/api/cards/${entry.id}`)
            .then(res => res.json())
            .then(card => ({
              ...card,
              count: entry.count || 1
            }))
            .catch(error => {
              console.error(`Failed to fetch card ${entry.id}:`, error);
              return null;
            })
        );
        
        const fetchedCards = await Promise.all(cardPromises);
        importedCards = fetchedCards.filter(Boolean);
      } else if (Array.isArray(deckData)) {
        // Legacy format - array of entries
        importedCards = deckData.map(entry => {
          if (entry.id && entry.name) {
            return entry;
          } else if (entry.imageUrl) {
            return {
              id: `legacy-${Date.now()}`,
              imageUrl: entry.imageUrl,
              count: entry.count || 1,
              name: 'Unknown Card'
            };
          }
          return null;
        }).filter(Boolean);
      }

      setCards(importedCards);
      setShowImport(false);
      setImportCode('');
    } catch (error) {
      console.error('Import error:', error);
      alert('Invalid deck code! Please check and try again.');
    }
  };

  const copyExport = () => {
    navigator.clipboard.writeText(exportCode).then(() => {
      const btn = document.getElementById('copyBtn');
      btn.textContent = 'Copied!';
      setTimeout(() => {
        btn.textContent = 'Copy Code';
      }, 2000);
    });
  };

  const exportToTTS = async () => {
    if (cards.length === 0) {
      alert('No cards to export!');
      return;
    }

    const totalCards = cards.reduce((sum, c) => sum + (parseInt(c.count) || 1), 0);
    if (totalCards !== 50) {
      alert(`Deck must have exactly 50 cards (current: ${totalCards}).`);
      return;
    }

    const deckNameInput = prompt('Name this deck for TTS export (used for file names):', 'Ultraman Deck');
    if (!deckNameInput) {
      return;
    }
    const safeDeckName = deckNameInput.trim().replace(/[^a-z0-9-_]+/gi, '_') || 'deck';

    try {
      // Use fixed back URL for all cards
      const BACK_URL = "https://steamusercontent-a.akamaihd.net/ugc/16420027240527538/483413ACD3746E956A903173304A14D98BA1A95A/";
      
      // Build CustomDeck, DeckIDs, and ContainedObjects using full-resolution TTS images
      const customDeck = {};
      const deckIDs = [];
      const containedObjects = [];
      let deckIdCounter = 1;

      cards.forEach(card => {
        const count = parseInt(card.count) || 1;
        const faceUrl = card.images?.tts?.full || card.images?.tts?.thumbnail || card.images?.blob?.full || card.images?.blob?.thumbnail || card.images?.original?.full || card.images?.original?.thumbnail;
        
        if (!faceUrl) {
          console.warn(`No image found for card: ${card.name}`);
          return;
        }

        const deckKey = deckIdCounter.toString();
        const deckEntry = {
          "FaceURL": faceUrl,
          "BackURL": BACK_URL,
          "NumWidth": 1,
          "NumHeight": 1,
          "BackIsHidden": true,
          "UniqueBack": false,
          "Type": 0
        };

        customDeck[deckKey] = deckEntry;

        for (let i = 0; i < count; i++) {
          const cardId = deckIdCounter * 100 + i;
          deckIDs.push(cardId);
          containedObjects.push({
            "GUID": Math.random().toString(16).slice(2, 8),
            "Name": "Card",
            "Transform": {
              "posX": -38.1626358,
              "posY": 1.69510412,
              "posZ": 13.9254141,
              "rotX": -3.36123918e-07,
              "rotY": 180.015976,
              "rotZ": 2.93983561e-07,
              "scaleX": 1.8,
              "scaleY": 1.0,
              "scaleZ": 1.8
            },
            "Nickname": `${card.name || 'Card'}${card.cardNumber ? ` - ${card.cardNumber}` : card.number ? ` - ${card.number}` : ''}`,
            "Description": "",
            "GMNotes": "",
            "ColorDiffuse": {
              "r": 0.713235259,
              "g": 0.713235259,
              "b": 0.713235259
            },
            "Tags": [
              `Card Type: ${card.type || ''}`,
              `Character Name: ${card.characterName || ''}`,
              `Level: ${card.level || ''}`,
              "limit: ",
              `Rarity: ${card.rarity || ''}`,
              `Type: ${card.subtype || ''}`
            ],
            "LayoutGroupSortIndex": 0,
            "Value": 0,
            "Locked": false,
            "Grid": true,
            "Snap": true,
            "IgnoreFoW": false,
            "MeasureMovement": false,
            "DragSelectable": true,
            "Autoraise": true,
            "Sticky": true,
            "Tooltip": true,
            "GridProjection": false,
            "HideWhenFaceDown": true,
            "Hands": true,
            "CardID": cardId,
            "SidewaysCard": false,
            "CustomDeck": {
              [deckKey]: deckEntry
            },
            "LuaScript": "",
            "LuaScriptState": "",
            "XmlUI": ""
          });
        }

        deckIdCounter++;
      });

      // Build TTS JSON structure
      const ttsData = {
        "SaveName": "",
        "Date": "",
        "VersionNumber": "",
        "GameMode": "",
        "GameType": "",
        "GameComplexity": "",
        "Tags": [],
        "Gravity": 0.5,
        "PlayArea": 0.5,
        "Table": "",
        "Sky": "",
        "Note": "",
        "TabStates": {},
        "LuaScript": "",
        "LuaScriptState": "",
        "XmlUI": "",
        "ObjectStates": [
          {
            "GUID": "4a0f84",
            "Name": "Deck",
            "Transform": {
              "posX": -38.1626358,
              "posY": 1.71205807,
              "posZ": 13.9254141,
              "rotX": -5.1007197e-07,
              "rotY": 180.015976,
              "rotZ": 1.57628949e-06,
              "scaleX": 1.8,
              "scaleY": 1.0,
              "scaleZ": 1.8
            },
            "Nickname": deckNameInput,
            "Description": "",
            "GMNotes": "",
            "ColorDiffuse": {
              "r": 0.713235259,
              "g": 0.713235259,
              "b": 0.713235259
            },
            "LayoutGroupSortIndex": 0,
            "Value": 0,
            "Locked": false,
            "Grid": true,
            "Snap": true,
            "IgnoreFoW": false,
            "MeasureMovement": false,
            "DragSelectable": true,
            "Autoraise": true,
            "Sticky": true,
            "Tooltip": true,
            "GridProjection": false,
            "HideWhenFaceDown": true,
            "Hands": false,
            "SidewaysCard": false,
            "DeckIDs": deckIDs,
            "CustomDeck": customDeck,
            "LuaScript": "",
            "LuaScriptState": "",
            "XmlUI": "",
            "ContainedObjects": containedObjects
          }
        ]
      };

      const zip = new JSZip();
      zip.file(`${safeDeckName}.json`, JSON.stringify(ttsData, null, 2));

      const firstImageUrl = containedObjects[0]?.CustomDeck?.[Object.keys(containedObjects[0]?.CustomDeck || {})[0]]?.FaceURL || cards[0]?.images?.tts?.full;
      if (firstImageUrl) {
        try {
          const response = await fetch(firstImageUrl);
          const arrayBuffer = await response.arrayBuffer();
          zip.file(`${safeDeckName}.png`, arrayBuffer);
        } catch (imgErr) {
          console.warn('Failed to fetch first image for zip PNG:', imgErr);
        }
      }

      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${safeDeckName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('TTS export error:', error);
      alert('Failed to export deck to TTS format: ' + error.message);
    }
  };

  const exportDeckImage = async () => {
    const cardGrid = document.getElementById('cardGrid');
    if (!cardGrid || cards.length === 0) {
      alert('No cards to export!');
      return;
    }

    // Save original styles
    const origPadding = cardGrid.style.padding;
    const origBg = cardGrid.style.background;
    const origRadius = cardGrid.style.borderRadius;
    const origShadow = cardGrid.style.boxShadow;
    
    // Save original image sources
    const imageElements = cardGrid.querySelectorAll('img');
    const originalSrcs = Array.from(imageElements).map(img => img.src);

   // Apply styles for export
    cardGrid.style.padding = '40px';

    try {
      // Pre-load all images as data URLs to bypass CORS
      const imagePromises = Array.from(imageElements).map((img, index) => {
        return new Promise((resolve) => {
          const tempImg = new Image();
          tempImg.crossOrigin = 'anonymous';
          
          tempImg.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = tempImg.width;
            canvas.height = tempImg.height;
            ctx.drawImage(tempImg, 0, 0);
            img.src = canvas.toDataURL('image/png');
            resolve();
          };
          
          tempImg.onerror = () => {
            // If loading fails, just resolve and continue with original
            console.warn(`Could not pre-load image: ${originalSrcs[index]}`);
            resolve();
          };
          
          tempImg.src = originalSrcs[index];
        });
      });
      
      await Promise.all(imagePromises);

      const canvas = await html2canvas(cardGrid, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
        scale: 2,
      });

      // Create final canvas with gradient
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;

      const gradient = ctx.createLinearGradient(0, 0, finalCanvas.width, finalCanvas.height);
      gradient.addColorStop(0, '#547ba6');
      gradient.addColorStop(1, '#f5f5f5');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);

      // Download
      const link = document.createElement('a');
      link.download = 'ultraman-deck.png';
      link.href = finalCanvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error(error);
      alert('Failed to generate deck image. Please try again.');
    } finally {
      // Restore original image sources and styles
      Array.from(imageElements).forEach((img, index) => {
        img.src = originalSrcs[index];
      });
      cardGrid.style.padding = origPadding;
      cardGrid.style.background = origBg;
      cardGrid.style.borderRadius = origRadius;
      cardGrid.style.boxShadow = origShadow;
    }
  };

  const searchCards = async (page = 0) => {
    if (!searchQuery.trim()) {
      setSearchError('Please enter a card name');
      return;
    }

    setSearchLoading(true);
    if (page === 0) {
      setSearchError('');
      setSearchResults([]);
      setTotalLoadedCards(0);
    }

    try {
      const offset = page === 0 ? 0 : totalLoadedCards;
      const response = await fetch('/api/search-cards', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          query: searchQuery, 
          offset, 
          limit: 20,
          cardTypes: selectedCardTypes.length > 0 ? selectedCardTypes : undefined,
          characters: selectedCharacters.length > 0 ? selectedCharacters : undefined,
          levels: selectedLevels.length > 0 ? selectedLevels : undefined
        }),
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      const newResults = page === 0 ? data.results : [...searchResults, ...(data.results || [])];
      setSearchResults(newResults);
      setSearchPage(page);
      setTotalLoadedCards(page === 0 ? (data.results || []).length : totalLoadedCards + (data.results || []).length);
      setHasMoreResults((data.results || []).length === 20);

      if (newResults.length === 0 && page === 0) {
        setSearchError('No cards found matching your search');
      }
    } catch (error) {
      console.error('Search error:', error);
      setSearchError('Failed to search cards. Make sure the backend is running.');
    } finally {
      setSearchLoading(false);
    }
  };

  const loadMoreCards = () => {
    if (!searchLoading && hasMoreResults) {
      searchCards(searchPage + 1);
    }
  };

  const handleSearchResultsScroll = (e) => {
    const container = e.target;
    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Load more when user scrolls within 200px of bottom
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadMoreCards();
    }
  };

  const fetchRecommendations = async (deckCards) => {
    if (deckCards.length === 0) {
      setRecommendations([]);
      return;
    }

    try {
      setRecommendationsLoading(true);
      const response = await fetch('/api/recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ deckCards, limit: 10 }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch recommendations');
      }

      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (error) {
      console.error('Recommendations error:', error);
    } finally {
      setRecommendationsLoading(false);
    }
  };

  const clearAllFilters = () => {
    setSelectedCardTypes([]);
    setSelectedCharacters([]);
    setSelectedLevels([]);
  };

  const drawInitialHand = () => {
    // Create a deck array with all cards duplicated by their count
    const fullDeck = [];
    cards.forEach(card => {
      const count = parseInt(card.count) || 1;
      for (let i = 0; i < count; i++) {
        fullDeck.push(card);
      }
    });

    // Shuffle using Fisher-Yates algorithm
    for (let i = fullDeck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [fullDeck[i], fullDeck[j]] = [fullDeck[j], fullDeck[i]];
    }

    // Draw first 6 cards
    const hand = fullDeck.slice(0, 6);
    const remaining = fullDeck.slice(6);

    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setHasDrawn(true);
  };

  const mulligan = () => {
    if (mulliganCount >= 1) {
      alert('You can only mulligan once!');
      return;
    }
    
    if (remainingDeck.length < 6) {
      alert('Not enough cards left in deck to mulligan!');
      return;
    }

    const hand = remainingDeck.slice(0, 6);
    const remaining = remainingDeck.slice(6);

    setDrawnCards(hand);
    setRemainingDeck(remaining);
    setMulliganCount(mulliganCount + 1);
  };

  const resetDraw = () => {
    setMulliganCount(0);
    drawInitialHand();
  };

  const updateCardCount = (index, delta) => {
    const newCards = [...cards];
    const currentCount = parseInt(newCards[index].count) || 1;
    const newCount = currentCount + delta;
    const cardNumber = newCards[index].cardNumber || newCards[index].number;
    const maxAllowed = getMaxCopiesAllowed(cardNumber);
    
    // Allow removal at 0, enforce max based on custom rules
    if (newCount < 0) return;
    if (newCount > maxAllowed) return;
    
    if (newCount === 0) {
      // Remove card from deck
      newCards.splice(index, 1);
    } else {
      newCards[index].count = newCount;
    }
    
    setCards(newCards);
  };

  const addCardFromSearch = (card) => {
    // Check if card already exists in deck by cardNumber (unique identifier)
    // cardNumber is section+bundleVersion+"-"+serial (e.g., "BP03-077")
    const uniqueCardNumber = card.number; // This is now computed as section+bundleVersion+"-"+serial
    const existingCardIndex = cards.findIndex(c => (c.cardNumber || c.number) === uniqueCardNumber);
    const maxAllowed = getMaxCopiesAllowed(uniqueCardNumber);
    
    let updatedCards;
    if (existingCardIndex !== -1) {
      // Card already exists - increment count (if not already at max allowed)
      updatedCards = [...cards];
      if (updatedCards[existingCardIndex].count < maxAllowed) {
        updatedCards[existingCardIndex].count += 1;
      }
    } else {
      // New card - add it with count of 1
      // Extract level from card type or level field
      let level = card.level;
      if (!level && card.type) {
        const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
        const levelMatch = typeText.match(/level\s*(\d+)|lv\.?\s*(\d+)|l(\d+)/i);
        level = levelMatch ? (levelMatch[1] || levelMatch[2] || levelMatch[3]) : null;
      }

      const normalizedCard = {
        id: card.id,
        name: card.name,
        number: card.number,
        cardNumber: card.number, // Unique identifier for deck limits (section+bundleVersion+"-"+serial)
        characterName: card.characterName || 'Unknown',
        rarity: typeof card.rarity === 'string' ? card.rarity : card.rarity?.value || '',
        type: typeof card.type === 'string' ? card.type : card.type?.value || '',
        feature: typeof card.feature === 'string' ? card.feature : card.feature?.value || '',
        level: level,
        images: card.images,
        count: 1
      };

      // Store the full card object with count - keep search results open
      updatedCards = [...cards, normalizedCard];
    }

    setCards(updatedCards);
    // Fetch recommendations based on updated deck
    fetchRecommendations(updatedCards);
  };

  // Chart helper functions
  const getLevelChartData = () => {
    const levels = Array.from({ length: 7 }, (_, i) => i + 1);
    const levelData = levels.map(level => {
      const key = `level${level}`;
      return deckValidation.stats.cardsByLevel[key] || 0;
    });

    console.log('Level Chart Data:', { stats: deckValidation.stats.cardsByLevel, data: levelData });

    return {
      labels: levels.map(l => `L${l}`),
      datasets: [
        {
          label: 'Cards by Level',
          data: levelData,
          backgroundColor: '#3c618a',
          borderColor: '#2a4563',
          borderWidth: 1,
          borderRadius: 4,
        }
      ]
    };
  };

  const getCharacterChartData = () => {
    const labels = Object.keys(deckValidation.stats.characterBreakdown);
    const data = Object.values(deckValidation.stats.characterBreakdown);

    console.log('Character Chart Data:', { breakdown: deckValidation.stats.characterBreakdown, labels, data });

    // Color palette for pie chart
    const colors = [
      '#3c618a', '#7a67ba', '#dc143c', '#ff6b9d', '#ffa500',
      '#51cf66', '#339af0', '#845ef7', '#d0bfff', '#ffd43b',
      '#ff922b', '#fa5252', '#748ffc', '#b197fc', '#91a7ff'
    ];

    return {
      labels: labels,
      datasets: [
        {
          label: 'Character Distribution',
          data: data,
          backgroundColor: colors.slice(0, labels.length),
          borderColor: '#fff',
          borderWidth: 2,
        }
      ]
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: { size: 11 },
          padding: 10,
          usePointStyle: true,
        }
      }
    }
  };

  const barChartOptions = {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 10,
        ticks: { stepSize: 2 }
      }
    }
  };

  return (
    <div className={`container${sidebarExpanded ? ' sidebar-expanded' : ''}`}>
      <div className="header">
        <h1>Ultra League Style Deck Visualizer</h1>
        <p>Ultraman Card Game Decklist</p>
      </div>

      <div className="main-wrapper">
        {/* Main Content Area (Left) */}
        <div className="main-content">
          <div className="controls">
            <h3>Deck Management</h3>
            <button onClick={() => setShowClearConfirm(true)}>Clear All</button>
            <button onClick={exportDeck}>Export Deck Code</button>
            <button onClick={exportDeckImage}>Generate Deck Image</button>
            <button onClick={exportToTTS} disabled={isMobile}>Export to TTS</button>
            <button onClick={() => setShowImport(!showImport)}>Import Deck Code</button>
            {showExportCode && <button id="copyBtn" onClick={copyExport}>Copy Code</button>}

            {showClearConfirm && (
              <div className="confirm-dialog">
                <p>Are you sure you want to clear all cards?</p>
                <button onClick={clearCards}>Yes, Clear All</button>
                <button onClick={() => setShowClearConfirm(false)}>Cancel</button>
              </div>
            )}

            {showImport && (
              <div className="import-section">
                <textarea
                  value={importCode}
                  onChange={(e) => setImportCode(e.target.value)}
                  placeholder="Paste your deck code here..."
                />
                <button onClick={() => importDeck()}>Load Deck</button>
                <button onClick={() => setShowImport(false)}>Cancel</button>
              </div>
            )}

            {showExportCode && (
              <div className="export-code">
                <p>{exportCode}</p>
              </div>
            )}

            <div className="instructions">
              <strong>Instructions:</strong> Use the sidebar to search for cards by name and add them to your deck.<br />
              <br />
              <strong>Export:</strong> Creates a compressed code of your deck. <strong>Import:</strong> Paste the export code to restore your deck.<br />
              <br />
              <strong>Generate Image:</strong> Creates a visual representation of your deck.<br />
              <br />
              <strong>Export to TTS:</strong> Downloads a .zip file that can be imported into Tabletop Simulator. Upload the contents to your TTS saved objects folder. <br />
              Example: "\Documents\My Games\Tabletop Simulator\Saves\Saved Objects"
            </div>
          </div>

          <div className="card-grid" id="cardGrid">
            {cards.sort((a, b) => {
              // Define card type order
              const getCardType = (card) => {
                const feature = typeof card.feature === 'string' ? card.feature : (card.feature?.value || '');
                const featureText = (feature || '').toLowerCase();
                
                if (featureText.includes('ultra_hero')) return 'Ultra Hero';
                if (featureText.includes('kaiju')) return 'Kaiju';
                if (featureText === 'scene') return 'Scene';
                return 'Other';
              };

              const typeOrder = { 'Ultra Hero': 0, 'Kaiju': 1, 'Scene': 2, 'Other': 3 };
              
              const typeA = getCardType(a);
              const typeB = getCardType(b);
              const typeCompare = typeOrder[typeA] - typeOrder[typeB];
              
              if (typeCompare !== 0) return typeCompare;
              
              // Sort by character name
              const charA = (a.characterName || '').toLowerCase();
              const charB = (b.characterName || '').toLowerCase();
              const charCompare = charA.localeCompare(charB);
              
              if (charCompare !== 0) return charCompare;
              
              // Sort by level (numeric)
              const levelA = parseInt(a.level || 0);
              const levelB = parseInt(b.level || 0);
              const levelCompare = levelA - levelB;
              
              if (levelCompare !== 0) return levelCompare;
              
              // Sort by card id
              return (a.number || '').localeCompare(b.number || '');
            }).map((card, index) => {
              const imageUrl = getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full);
              const rarityText = typeof card.rarity === 'string' ? card.rarity : card.rarity?.value || '';
              const typeText = typeof card.type === 'string' ? card.type : card.type?.value || '';
              const featureValue = card.feature || '';
              const isScene = featureValue === 'scene';
              const isHovered = hoveredCardIndex === index;
              
              return (
                <div 
                  key={index} 
                  className={`card-wrapper${isScene ? ' scene-card' : ''}`}
                  onMouseEnter={() => setHoveredCardIndex(index)}
                  onMouseLeave={() => setHoveredCardIndex(null)}
                >
                  {imageUrl && <img src={imageUrl} alt={card.name} className="card-image" />}
                  <div className="card-info">
                    <div className="card-name">{card.name}</div>
                    <div className="card-meta">{card.number}</div>
                    {rarityText && <div className="card-rarity">{rarityText}</div>}
                    {typeText && <div className="card-type">{typeText}</div>}
                  </div>
                  <div className="card-count">x{card.count}</div>
                  
                  {isHovered && (
                    <div className="card-overlay">
                      <div className="overlay-content">
                        <h4>{card.name}</h4>
                        {card.number && <p>{card.number}</p>}
                        <div className="overlay-controls">
                          <button 
                            className="count-btn decrease-btn"
                            onClick={() => updateCardCount(index, -1)}
                            title="Decrease count or remove"
                          >
                            −
                          </button>
                          <span className="count-display">x{card.count}</span>
                          <button 
                            className="count-btn increase-btn"
                            onClick={() => updateCardCount(index, 1)}
                            disabled={card.count >= getMaxCopiesAllowed(card.cardNumber || card.number)}
                            title="Increase count"
                          >
                            +
                          </button>
                        </div>
                        <button
                          className="delete-card-btn"
                          onClick={() => updateCardCount(index, -card.count)}
                          title="Remove card from deck"
                        >
                          🗑 Remove Card
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Search Sidebar (Right - Always Pinned) */}
      <div className={`sidebar-pinned${sidebarExpanded ? ' sidebar-expanded' : ''}`}>
        <div className="sidebar-header" onClick={() => setSidebarExpanded(!sidebarExpanded)} style={{ cursor: 'pointer' }}>
          <h3>🔍 Card Search</h3>
          <button 
            className="sidebar-expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              setSidebarExpanded(!sidebarExpanded);
            }}
            title={sidebarExpanded ? "Collapse" : "Expand"}
          >
            {sidebarExpanded ? '✕' : '⌃'}
          </button>
        </div>

        <div className="sidebar-content">
          {/* Deck Validator Section */}
          <div className="sidebar-section deck-validator">
            <div className="section-header">
              <h4>📊 Deck Stats</h4>
              <button 
                className="collapse-btn" 
                onClick={() => setCollapseStats(!collapseStats)}
                title={collapseStats ? "Expand" : "Collapse"}
              >
                {collapseStats ? '▶' : '▼'}
              </button>
            </div>
            {!collapseStats && (<div className={`stats-box ${deckValidation.errors.length > 0 ? 'has-errors' : 'valid'}`}>
              <div className="stat-item">
                <span className="stat-label">Total Cards:</span>
                <span className={`stat-value ${deckValidation.stats.totalCards > 50 ? 'error' : ''}`}>
                  {deckValidation.stats.totalCards}/50
                </span>
              </div>
              
              {deckValidation.stats.totalCards > 0 && (
                <>
                  {/* Level Distribution Bar Chart */}
                  <div className="chart-container level-chart">
                    <h5>Level Distribution</h5>
                    <Bar data={getLevelChartData()} options={barChartOptions} />
                  </div>

                  {/* Character Distribution Pie Chart */}
                  <div className="chart-container character-chart">
                    <h5>Character Distribution</h5>
                    <Pie data={getCharacterChartData()} options={chartOptions} />
                  </div>
                </>
              )}

              {deckValidation.stats.sceneCards > 0 && (
                <div className="stat-item">
                  <span className="stat-label">Scene Cards:</span>
                  <span className="stat-value">{deckValidation.stats.sceneCards}</span>
                </div>
              )}
            </div>)}

            {!collapseStats && (<>
            {/* Error Messages */}
            {deckValidation.errors.length > 0 && (
              <div className="validator-errors">
                <h5>⚠️ Deck Errors:</h5>
                {deckValidation.errors.map((error, idx) => (
                  <div key={idx} className="error-message">{error}</div>
                ))}
              </div>
            )}

            {deckValidation.errors.length === 0 && deckValidation.stats.totalCards > 0 && (
              <div className="validator-success">
                ✅ Deck is valid!
              </div>
            )}
            </>)}
          </div>

          {/* Search Section */}
          <div className="sidebar-section search-section">
            <div className="section-header">
              <h4>🔍 Search Cards</h4>
              <button 
                className="collapse-btn" 
                onClick={() => setCollapseSearch(!collapseSearch)}
                title={collapseSearch ? "Expand" : "Collapse"}
              >
                {collapseSearch ? '▶' : '▼'}
              </button>
            </div>
            {!collapseSearch && <>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && searchCards()}
              placeholder="Search by card name..."
            />
            <button onClick={() => { setSearchPage(0); setTotalLoadedCards(0); searchCards(0); }} disabled={searchLoading} className="search-btn">
              {searchLoading ? 'Searching...' : 'Search'}
            </button>

            {/* Filters Section */}
            <div className="filters-container">
              <button 
                className="filters-toggle-btn"
                onClick={() => setCollapseFilters(!collapseFilters)}
              >
                🔽 Filters {selectedCardTypes.length + selectedCharacters.length + selectedLevels.length > 0 && `(${selectedCardTypes.length + selectedCharacters.length + selectedLevels.length})`}
              </button>

              {!collapseFilters && (
                <div className="filters-panel">
                  {/* Card Type Filter */}
                  <div className="filter-group">
                    <h5>Card Type</h5>
                    <select
                      className="filter-dropdown"
                      value={selectedCardTypes[0] || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          setSelectedCardTypes([e.target.value]);
                        } else {
                          setSelectedCardTypes([]);
                        }
                      }}
                    >
                      <option value="">All Types</option>
                      <option value="Ultra Hero">Ultra Hero</option>
                      <option value="Kaiju">Kaiju</option>
                      <option value="Scene">Scene</option>
                    </select>
                  </div>

                  {/* Character Filter */}
                  <div className="filter-group">
                    <h5>Character Name</h5>
                    <input
                      type="text"
                      className="character-filter-input"
                      placeholder="Filter by character..."
                      value={selectedCharacters.join(', ')}
                      onChange={(e) => {
                        const chars = e.target.value.split(',').map(c => c.trim()).filter(Boolean);
                        setSelectedCharacters(chars);
                      }}
                    />
                  </div>

                  {/* Level Filter */}
                  <div className="filter-group">
                    <h5>Card Level</h5>
                    <select
                      className="filter-dropdown"
                      value={selectedLevels[0] || ''}
                      onChange={(e) => {
                        if (e.target.value) {
                          setSelectedLevels([e.target.value]);
                        } else {
                          setSelectedLevels([]);
                        }
                      }}
                    >
                      <option value="">All Levels</option>
                      {['1', '2', '3', '4', '5', '6', '7'].map(level => (
                        <option key={level} value={level}>
                          Level {level}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button className="clear-filters-btn" onClick={clearAllFilters}>
                    Clear All Filters
                  </button>
                </div>
              )}
            </div>

            {searchError && <div className="search-error">{searchError}</div>}

            {searchResults.length > 0 && (
              <div className="search-results" onScroll={handleSearchResultsScroll}>
                {searchResults.map((card, index) => (
                  <div 
                    key={index} 
                    className={`search-result-item${card.feature === 'scene' ? ' scene-card' : ''}`}
                    onClick={() => addCardFromSearch(card)}
                  >
                    <img
                      src={getProxyImageUrl(card.images?.blob?.full || card.images?.original?.full)}
                      alt={card.name}
                      className="result-thumbnail"
                      loading="lazy"
                    />
                    {sidebarExpanded ? (
                      // Expanded state: overlay display
                      <div className="search-result-overlay">
                        <div className="overlay-content">
                          <h4>{card.name}</h4>
                          <p className="result-number">{card.number}</p>
                          <p className="result-meta">
                            {card.characterName && <span className="character-tag">{card.characterName}</span>}
                            {typeof card.rarity === 'string' ? (
                              <span className="rarity-tag">{card.rarity}</span>
                            ) : card.rarity?.description && (
                              <span className="rarity-tag">{card.rarity.description}</span>
                            )}
                          </p>
                          <button className="add-btn-overlay">
                            Add to Deck
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Collapsed state: side-by-side display like recommendations
                      <>
                        <div className="result-info">
                          <strong>{card.name}</strong>
                          <p className="result-number">{card.number}</p>
                          <p className="result-meta">
                            {card.characterName && <span className="character-tag">{card.characterName}</span>}
                            {typeof card.rarity === 'string' ? (
                              <span className="rarity-tag">{card.rarity}</span>
                            ) : card.rarity?.description && (
                              <span className="rarity-tag">{card.rarity.description}</span>
                            )}
                          </p>
                        </div>
                        <button className="add-btn">
                          Add
                        </button>
                      </>
                    )}
                  </div>
                ))}
                {searchLoading && searchResults.length > 0 && (
                  <div className="loading-indicator">
                    <div className="spinner"></div>
                    <span>Loading more cards...</span>
                  </div>
                )}
                {!hasMoreResults && searchResults.length > 0 && (
                  <div className="end-of-results">
                    No more cards to load
                  </div>
                )}
              </div>
            )}
            </>
            }
          </div>

          {/* Recommendations Section */}
          <div className="sidebar-section recommendations-section">
            <div className="section-header">
              <h4>💡 Recommended Cards</h4>
              <button 
                className="collapse-btn" 
                onClick={() => setCollapseRecommendations(!collapseRecommendations)}
                title={collapseRecommendations ? "Expand" : "Collapse"}
              >
                {collapseRecommendations ? '▶' : '▼'}
              </button>
            </div>
            {!collapseRecommendations && (
              <>
                {recommendationsLoading && (
                  <div className="loading-indicator">
                    <div className="spinner"></div>
                    <span>Loading recommendations...</span>
                  </div>
                )}
                {!recommendationsLoading && recommendations.length === 0 && cards.length > 0 && (
                  <div className="no-recommendations">
                    No recommendations found for your deck
                  </div>
                )}
                {!recommendationsLoading && recommendations.length > 0 && (
                  <div className="recommendations-list">
                    {recommendations.map((card, index) => (
                      <div key={index} className={`recommendation-item${card.feature === 'scene' ? ' scene-card' : ''}`} onClick={() => addCardFromSearch(card)}>
                        <img
                          src={getProxyImageUrl(card.images?.blob?.thumbnail || card.images?.original?.thumbnail)}
                          alt={card.name}
                          className="result-thumbnail"
                          loading="lazy"
                        />
                        <div className="result-info">
                          <strong>{card.name}</strong>
                          <p className="result-number">{card.number}</p>
                          <p className="result-meta">
                            {card.characterName && <span className="character-tag">{card.characterName}</span>}
                            {card.rarity?.description && <span className="rarity-tag">{card.rarity.description}</span>}
                          </p>
                          <p className="score-display">Score: {card.recommendationScore.toFixed(1)}</p>
                        </div>
                        <div className="recommendation-overlay">
                          <div className="overlay-content">
                            <h4>{card.name}</h4>
                            <p className="result-number">{card.number}</p>
                            <p className="result-meta">
                              {card.characterName && <span className="character-tag">{card.characterName}</span>}
                              {card.rarity?.description && <span className="rarity-tag">{card.rarity.description}</span>}
                            </p>
                            <p className="score-display">Score: {card.recommendationScore.toFixed(1)}</p>
                            <button onClick={() => addCardFromSearch(card)} className="add-btn-overlay">
                              Add to Deck
                            </button>
                          </div>
                        </div>
                        <button onClick={() => addCardFromSearch(card)} className="add-btn">
                          Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Draw Simulator Section */}
          <div className="sidebar-section draw-simulator-section">
            <div className="section-header">
              <h4>🎲 Draw Simulator</h4>
              <button 
                className="collapse-btn" 
                onClick={() => setCollapseDrawSimulator(!collapseDrawSimulator)}
                title={collapseDrawSimulator ? "Expand" : "Collapse"}
              >
                {collapseDrawSimulator ? '▶' : '▼'}
              </button>
            </div>
            {!collapseDrawSimulator && (
              <>
                {cards.length === 0 ? (
                  <p className="no-cards-message">Add cards to your deck to use the draw simulator</p>
                ) : (
                  <>
                    {!hasDrawn ? (
                      <button className="draw-btn" onClick={drawInitialHand}>
                        Draw Opening Hand
                      </button>
                    ) : (
                      <>
                        <div className="drawn-cards-container">
                          <h5>Hand ({drawnCards.length})</h5>
                          <div className="drawn-cards">
                            {drawnCards.map((card, index) => (
                              <div key={index} className="drawn-card-item">
                                <img
                                  src={getProxyImageUrl(card.images?.blob?.thumbnail || card.images?.original?.thumbnail)}
                                  alt={card.name}
                                  className="drawn-card-image"
                                  title={card.name}
                                />
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="draw-actions">
                          <button 
                            className="mulligan-btn" 
                            onClick={mulligan}
                            disabled={mulliganCount >= 1 || remainingDeck.length < 6}
                          >
                            Mulligan {mulliganCount > 0 && '(Used)'}
                          </button>
                          <button className="reset-draw-btn" onClick={resetDraw}>
                            New Hand
                          </button>
                        </div>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <button 
        className="sidebar-toggle-btn" 
        onClick={() => setSidebarExpanded(!sidebarExpanded)}
        title={sidebarExpanded ? "Collapse sidebar" : "Expand sidebar"}
      >
        {sidebarExpanded ? '▶' : '◀'}
      </button>

      <div className="footer">
        <p>© 2025 Gardeno Games Deck Visualizer. Ultraman Card Game and the Ultraman IP are trademarks of Tsuburaya Productions.</p>
      </div>
    </div>
  );
}

export default DeckVisualizer;
